import Cookies from 'js-cookie'

export type CookieInterface = ReturnType<typeof createCookieInterface>

/**
 * This does not set the cookie in the browser, just creates an interface for CRUD
 * operations on the cookie
 *
 * @param name Name of the cookie to be stored in the browser
 * @param domain Domain of the cookie. Defaults to the current domain if not set. Some cookies
 * belong to the .medicare.gov domain instead of env.medicare.gov
 * @returns Cookie object representation
 */
const createCookieInterface = (
  name: string,
  domain?: '.medicare.gov' | undefined
) => {
  // Ignore domain in local and tests because localhost does not allow other domains
  const useCookieAttributes = domain !== undefined && process.env.NODE_ENV === 'production'
  const CookiesWithAttributes = useCookieAttributes ? Cookies.withAttributes({ domain }) : Cookies

  return {
    name,
    domain,
    set: (value: string) => CookiesWithAttributes.set(name, value),
    get: () => CookiesWithAttributes.get(name),
    isSet: () => CookiesWithAttributes.get(name) !== undefined,
    remove: () => CookiesWithAttributes.remove(name)
  }
}

const slsCsrfName = 'sls-csrf'
/**
 * Collection of all cookie interfaces in use by the SPA
 */
export const cookieInterfaces = {
  acceptedMsaDisclaimer: createCookieInterface('accepted-msa-disclaimer'),
  acceptedMspDisclaimer: createCookieInterface('accepted-msp-disclaimer'),
  showMAContent: createCookieInterface('showMAContent'),
  session: createCookieInterface('max-session-id'),
  fetchedReqToken: createCookieInterface('max-fetched-token'),
  slsCsrf: createCookieInterface(slsCsrfName, '.medicare.gov'),
  oifOraOsfsSession: createCookieInterface('ORA_OSFS_SESSION', '.medicare.gov'),
  oifNgdOifJSessionId: createCookieInterface('NGDOIFJSessionId', '.medicare.gov'),
  oifJSession: createCookieInterface('JSESSIONID', '.medicare.gov'),
  refreshNGD: createCookieInterface('refresh-ngd', '.medicare.gov')
}

/**
 * Clears cookies using cookie interfaces in use by the SPA
 * - does not clear sls-csrf. We don't not manage this cookie. We only read.
 */
export const clearMedicareMyCookies = () => {
  Object.values(cookieInterfaces).forEach((cookieInterface) => {
    // Ignore slsCsrf - we don't actually set or remove this cookie. We only get it for sls requests
    if (cookieInterface.name !== slsCsrfName) {
      cookieInterface.remove()
    }
  })
}
